import * as React from 'react'
import { Suspense, lazy } from 'react'
import { Switch, Route } from 'react-router-dom'
import { PrivateRoute } from '$views/components/PrivateRoute'
import history from '$utils/history'
import { routeProvider } from '$utils/routeProvider'
import { tagManager } from '$infrastructure/tagManager/tagManager'
import { Spinner } from '$views/components/Spinner'
import { chatClient } from '$infrastructure/cometChat/cometChatClient'
import { CHAT_LISTENERS } from '$utils/enumsChat'
import { isUserAuthenticated } from '$services/session/isAuthenticated/isAuthenticated.service'

const Login = lazy(() => {
  return import(/* webpackChunkName: "Login" */ './Login')
})
const HomeNativeApp = lazy(() => {
  return import(/* webpackChunkName: "HomeNativeApp" */ '$views/Home/HomeNativeApp')
})
const LoginRouter = lazy(() => {
  return import(/* webpackChunkName: "LoginRouter" */ '$views/Login/Login.router')
})
const RecommendationsForms = lazy(() => {
  return import(/* webpackChunkName: "RecommendationsForms" */ './RecommendationsForms')
})
const ChildRecommendationForm = lazy(() => {
  return import(/* webpackChunkName: "ChildRecommendationForm" */ './RecommendationsForms/ChildRecommendationForm')
})
const ResidenceConfirmStep = lazy(() => {
  return import(
    /* webpackChunkName: "ResidenceConfirmStep" */ './RecommendationsForms/ResidenceForm/ResidenceConfirmStep'
  )
})
const RecruiterRecommendationForm = lazy(() => {
  return import(
    /* webpackChunkName: "RecruiterRecommendationForm" */ './RecommendationsForms/RecruiterRecommendationForm'
  )
})
const BroadcastForm = lazy(() => {
  return import(/* webpackChunkName: "BroadcastForm" */ './RecommendationsForms/BroadcastForm')
})
const CheckoutForm = lazy(() => {
  return import(/* webpackChunkName: "CheckoutForm" */ './LongTermRecommendation/CheckoutForm')
})
const BroadcastConfirmationStep = lazy(() => {
  return import(
    /* webpackChunkName: "BroadcastConfirmationStep" */ './RecommendationsForms/BroadcastForm/ConfirmationStep'
  )
})
const ManualRequestForm = lazy(() => {
  return import(/* webpackChunkName: "ManualRequestForm" */ '$views/ManualRequestForm')
})
const ResidenceForm = lazy(() => {
  return import(/* webpackChunkName: "ResidenceForm" */ '$views/RecommendationsForms/ResidenceForm')
})
const TermsOfService = lazy(() => {
  return import(/* webpackChunkName: "TermsOfService" */ '$views/TermsOfService')
})
const CaretakerRecommendation = lazy(() => {
  return import(/* webpackChunkName: "CaretakerRecommendation" */ './CaretakerRecommendation')
})
const UserAreaRouter = lazy(() => {
  return import(/* webpackChunkName: "UserAreaRouter" */ '$views/UserArea/UserArea.router')
})
const CustomerHomeUserAreaRouter = lazy(() => {
  return import(
    /* webpackChunkName: "CustomerHomeUserAreaRouter" */ '$views/UserArea/Customer/CustomerHomeUserArea/CustomerHomeUserArea.router'
  )
})
const ConfirmServiceLengthRouter = lazy(() => {
  return import(/* webpackChunkName: "ConfirmServiceLengthRouter" */ './ConfirmServiceLength')
})
const ProfileRouter = lazy(() => {
  return import(/* webpackChunkName: "ProfileRouter" */ '$views/Profile/Profile.router')
})
const CaretakerList = lazy(() => {
  return import(/* webpackChunkName: "CaretakerList" */ '$views/CaretakerList')
})
const Error404 = lazy(() => {
  return import(/* webpackChunkName: "Error404" */ './Error404')
})
const CaretakerCard = lazy(() => {
  return import(/* webpackChunkName: "CaretakerCard" */ '$views/CaretakerCard')
})
const LongTermCaretakerRecommendation = lazy(() => {
  return import(
    /* webpackChunkName: "LongTermCaretakerRecommendation" */ '$views/LongTermRecommendation/LongTermCaretakerRecommendation'
  )
})
const Cookies = lazy(() => {
  return import(/* webpackChunkName: "Cookies" */ './Cookies')
})
const PrivacyPolicyInfo = lazy(() => {
  return import(/* webpackChunkName: "PrivacyPolicyInfo" */ '$views/PrivacyPolicyInfo/PrivacyPolicyInfo')
})
const CreateCoupon = lazy(() => {
  return import(/* webpackChunkName: "CreateCoupon" */ '$views/UserArea/Admin/Coupon/CreateCoupon')
})
const PremiumRouter = lazy(() => {
  return import(/* webpackChunkName: "PremiumRouter" */ '$views/Premium/Premium.router')
})
const ProRouter = lazy(() => {
  return import(/* webpackChunkName: "ProRouter" */ '$views/Pro/Pro.router')
})
const OffSessionPayment = lazy(() => {
  return import(/* webpackChunkName: "OffSessionPayment" */ '$views/PaymentDetails/OffSessionPayment')
})
const OnSessionPayment = lazy(() => {
  return import(/* webpackChunkName: "OnSessionPayment" */ '$views/PaymentDetails/OnSessionPayment')
})
const CheckoutPayment = lazy(() => {
  return import(/* webpackChunkName: "CheckoutPayment" */ '$views/PaymentDetails/CheckoutPayment')
})
const ExtraCharge = lazy(() => {
  return import(/* webpackChunkName: "ExtraCharge" */ '$views/PaymentDetails/ExtraCharge')
})
const RegisterRouter = lazy(() => {
  return import(/* webpackChunkName: "RegisterRouter" */ '$views/Register/Register.router')
})
const CaretakerRegistration = lazy(() => {
  return import(/* webpackChunkName: "CaretakerRegistration" */ '$views/CaretakerRegistration')
})
const CustomerRegistration = lazy(() => {
  return import(/* webpackChunkName: "CustomerRegistration" */ '$views/CustomerRegistration')
})
const CustomerCareDurationChoice = lazy(() => {
  return import(
    /* webpackChunkName: "CustomerCareDurationChoice" */ '$views/UserArea/Customer/CustomerCareDurationChoice'
  )
})
const LeadRouter = lazy(() => {
  return import(/* webpackChunkName: "LeadRouter" */ '$views/Lead/Lead.router')
})
const Checkout = lazy(() => {
  return import(/* webpackChunkName: "Checkout" */ '$views/Checkout')
})
const FullMapCaretakers = lazy(() => {
  return import(/* webpackChunkName: "FullMapCaretakers" */ '$views/FullMapCaretakers')
})
const OffersForCaretaker = lazy(() => {
  return import(/* webpackChunkName: "OffersForCaretaker" */ '$views/OffersForCaretaker')
})
const ChatRouter = lazy(() => {
  return import(/* webpackChunkName: "ChatRouter" */ '$views/Chat')
})
const PasswordRecovery = lazy(() => {
  return import(/* webpackChunkName: "PasswordRecovery" */ '$views/PasswordRecovery')
})
const ConfirmPasswordRecovery = lazy(() => {
  return import(/* webpackChunkName: "ConfirmPasswordRecovery" */ '$views/ConfirmPasswordRecovery')
})
const CaretakerExternalReview = lazy(() => {
  return import(/* webpackChunkName: "CaretakerExternalReview" */ '$views/Reviews/CaretakerExternalReview')
})
const CaretakerReview = lazy(() => {
  return import(/* webpackChunkName: "CaretakerReview" */ '$views/Reviews/CaretakerReview')
})
const CustomerReview = lazy(() => {
  return import(/* webpackChunkName: "CustomerReview" */ '$views/Reviews/CustomerReview')
})
const ResumeStep = lazy(() => {
  return import(/* webpackChunkName: "ResumeStep" */ '$views/RecommendationsForms/RecommendationForm/ResumeStep')
})
const ChildResumeStep = lazy(() => {
  return import(
    /* webpackChunkName: "ChildResumeStep" */ '$views/RecommendationsForms/ChildRecommendationForm/ChildResumeStep'
  )
})
const ResumeStepManual = lazy(() => {
  return import(/* webpackChunkName: "ResumeStepManual" */ '$views/ManualRequestForm/ResumeStepManual')
})
const ConfirmStep = lazy(() => {
  return import(
    /* webpackChunkName: "ConfirmStep" */ '$views/RecommendationsForms/RecruiterRecommendationForm/ConfirmStep'
  )
})
const ConfirmDeleteAccount = lazy(() => {
  return import(/* webpackChunkName: "ConfirmDeleteAccount" */ '$views/ConfirmDeleteAccount')
})
const ExpiredConfirmService = lazy(() => {
  return import(/* webpackChunkName: "ExpiredConfirmService" */ './ConfirmServiceLength/ExpiredConfirmService')
})
const RecruiterConfirmSelect = lazy(() => {
  return import(
    /* webpackChunkName: "RecruiterConfirmSelect" */ '$views/RecommendationsForms/RecruiterRecommendationForm/RecruiterConfirmSelect'
  )
})
const PaymentDetailsConfirmation = lazy(() => {
  return import(/* webpackChunkName: "PaymentDetailsConfirmation" */ './PaymentDetails/PaymentDetailsConfirmation')
})
const CaretakerServiceResponse = lazy(() => {
  return import(/* webpackChunkName: "CaretakerServiceResponse" */ '$views/UserArea/Caretaker/CaretakerServiceResponse')
})
const RecruitingCaretakerResponse = lazy(() => {
  return import(
    /* webpackChunkName: "RecruitingCaretakerResponse" */ '$views/RecommendationsForms/RecruiterRecommendationForm/RecruitingCaretakerResponse'
  )
})
const UnsubscribeEmails = lazy(() => {
  return import(/* webpackChunkName: "UnsubscribeEmails" */ '$views/UnsubscribeEmails')
})

interface WindowWithNativeProps extends Window {
  ReactNativeWebView?: any
}

const Root: React.FC = () => {
  const windowsWithNativeProps: WindowWithNativeProps = window

  const removeChatListeners = () => {
    if (isUserAuthenticated()) {
      chatClient.removeMessageListener(CHAT_LISTENERS.appListenerId)
      chatClient.removeMessageListener(CHAT_LISTENERS.loginListenerId)
    }
  }

  React.useEffect(() => {
    tagManager.push({ event: 'pageview' })

    history.listen((location, action) => {
      tagManager.push({ event: 'pageview' })
    })

    if (isUserAuthenticated()) {
      chatClient.markLastMessagesAsDelivered()
      chatClient.addMessageListener(
        CHAT_LISTENERS.appListenerId,
        chatClient.messageListener({
          onTextMessageReceived: textMessage => {
            chatClient.markAsDelivered(textMessage)
          },
        }),
      )
    }

    return () => removeChatListeners()
  }, [])

  return (
    <>
      <Suspense fallback={<Spinner />}>
        <Switch>
          {windowsWithNativeProps.ReactNativeWebView ? (
            <Route path="/" exact={true} component={HomeNativeApp} />
          ) : (
            <Route path="/" exact={true} component={Login} />
          )}
          <PrivateRoute path="/profile" component={ProfileRouter} />
          <PrivateRoute path="/mis-servicios" component={UserAreaRouter} />
          <PrivateRoute path="/mi-area-privada" component={CustomerHomeUserAreaRouter} />
          <PrivateRoute path="/ofertas" component={OffersForCaretaker} />
          <PrivateRoute
            path="/confirm-delete-account/:token"
            render={({ match: innerMatch }) => {
              return <ConfirmDeleteAccount token={innerMatch.params.token} />
            }}
          />
          <Route path="/login" component={LoginRouter} />
          <Route path="/registro" component={RegisterRouter} />
          <Route path="/cuidador/registro" exact={true} component={CaretakerRegistration} />
          <Route path="/cliente/registro" exact={true} component={CustomerRegistration} />
          <Route
            path="/cuidadores/:caretakerId"
            exact={true}
            render={({ match: innerMatch }) => {
              return <CaretakerCard caretakerId={innerMatch.params.caretakerId} />
            }}
          />

          <Route path="/restablecer/contrasena" exact component={PasswordRecovery} />

          <Route
            path="/restablecer/contrasena/confirmacion/:token"
            exact
            render={({ match: innerMatch }) => <ConfirmPasswordRecovery token={innerMatch.params.token} />}
          />

          <PrivateRoute path="/confirmacion-duracion-servicio" component={ConfirmServiceLengthRouter} />
          <PrivateRoute path="/servicio-tiempo-expirado" component={ExpiredConfirmService} />
          <Route path="/politica-privacidad" exact={true} component={PrivacyPolicyInfo} />
          <Route path="/condiciones-generales" exact={true} component={TermsOfService} />

          <Route path="/cookies" exact={true} component={Cookies} />
          <Route
            path="/ayuda"
            exact={true}
            render={() => {
              window.location.assign(routeProvider.help)
              return null
            }}
          />
          <Route
            path="/recomendacion"
            exact={true}
            render={() => {
              window.location.assign(routeProvider.recommendation)
              return null
            }}
          />
          <Route path="/recomendacion-cuidador" exact={true} component={RecommendationsForms} />
          <Route
            path="/formulario-residencias/:caretakerId?"
            exact
            render={({ match: innerMatch }) => <ResidenceForm caretakerId={innerMatch.params.caretakerId} />}
          />
          <Route path="/busqueda-abierta" exact={true} component={BroadcastForm} />
          <Route path="/formulario-kids" exact={true} component={ChildRecommendationForm} />
          <PrivateRoute path="/formulario-seleccion" exact={true} component={RecruiterRecommendationForm} />
          {(process.env.REACT_APP_CHECKOUT_BROADCAST as string) === 'true' && (
            <PrivateRoute
              path="/formulario-checkout/:caretakerId"
              exact={true}
              render={({ match: innerMatch }) => <CheckoutForm caretakerId={innerMatch.params.caretakerId} />}
            />
          )}
          <PrivateRoute path="/solicitud-seleccion-enviada" exact={true} component={ConfirmStep} />
          <PrivateRoute
            path="/confirmacion-seleccion/:requestId"
            exact
            render={({ match: innerMatch }) => <RecruiterConfirmSelect requestId={innerMatch.params.requestId} />}
          />
          <PrivateRoute
            path="/servicio-largo-plazo/:serviceId/disponibilidad"
            exact
            render={({ match: innerMatch }) => <RecruitingCaretakerResponse serviceId={innerMatch.params.serviceId} />}
          />
          <PrivateRoute
            path="/servicio/:serviceId/disponibilidad"
            exact
            render={({ match: innerMatch }) => <CaretakerServiceResponse serviceId={innerMatch.params.serviceId} />}
          />
          <Route path="/solicitud-enviada" exact={true} component={ResumeStep} />
          <Route path="/solicitud-enviada-kids" exact={true} component={ChildResumeStep} />
          <Route path="/solicitud-enviada-cuidador" exact={true} component={ResumeStepManual} />
          <Route
            path="/solicitud/:caretakerId"
            exact
            render={({ match: innerMatch }) => <ManualRequestForm caretakerId={innerMatch.params.caretakerId} />}
          />
          <Route
            path="/recomendacion-cuidador/:serviceId"
            exact={true}
            render={({ match: innerMatch }) => {
              return <CaretakerRecommendation serviceId={innerMatch.params.serviceId} />
            }}
          />
          <Route
            path="/recomendacion-largo-plazo/:serviceId"
            exact={true}
            render={({ match: innerMatch }) => {
              return <LongTermCaretakerRecommendation serviceId={innerMatch.params.serviceId} />
            }}
          />
          <Route
            path="/checkout/:caretakerId/:requestId"
            exact={true}
            render={({ match: innerMatch }) => {
              return <Checkout caretakerId={innerMatch.params.caretakerId} requestId={innerMatch.params.requestId} />
            }}
          />
          <Route path="/recoger-lead" component={LeadRouter} />
          <Route path="/solicitud-enviada-busqueda-abierta" exact component={BroadcastConfirmationStep} />
          <Route path="/solicitud-enviada-residencia" exact component={ResidenceConfirmStep} />
          <Route path="/tipo-recomendacion" exact={true} component={CustomerCareDurationChoice} />
          <Route
            path="/servicio/:requestId/valoracion-a-cuidador"
            exact={true}
            render={({ match: innerMatch }) => {
              return <CaretakerReview requestId={innerMatch.params.requestId} />
            }}
          />
          <Route
            path="/usuario/:userId/valoracion-a-cuidador"
            exact={true}
            render={({ match: innerMatch }) => {
              return <CaretakerExternalReview userId={innerMatch.params.userId} />
            }}
          />
          <Route
            path="/servicio/:requestId/valoracion-a-cliente"
            exact={true}
            render={({ match: innerMatch }) => {
              return <CustomerReview requestId={innerMatch.params.requestId} />
            }}
          />
          <Route path="/premium" component={PremiumRouter} />
          <Route path="/pro" component={ProRouter} />
          <Route
            path="/confirmar-reserva/:requestId"
            exact={true}
            render={({ match: innerMatch }) => {
              return <OffSessionPayment requestId={innerMatch.params.requestId} />
            }}
          />
          <Route
            path="/confirmar-reserva-fallida/:requestId"
            exact={true}
            render={({ match: innerMatch }) => {
              return <OnSessionPayment requestId={innerMatch.params.requestId} />
            }}
          />
          <Route
            path="/confirmar-reserva-checkout/:caretakerId"
            exact={true}
            render={({ match: innerMatch }) => {
              return <CheckoutPayment caretakerId={innerMatch.params.caretakerId} />
            }}
          />
          <Route path="/reserva-confirmada" exact={true} component={PaymentDetailsConfirmation} />
          <Route
            path="/gastos-extra/:requestId"
            exact={true}
            render={({ match: innerMatch }) => {
              return <ExtraCharge requestId={innerMatch.params.requestId} />
            }}
          />
          <Route
            path="/acceso"
            exact={true}
            render={() => {
              history.push(routeProvider.login)
              return null
            }}
          />
          <Route
            path="/como-funciona/soy-cuidador"
            exact={true}
            render={() => {
              window.location.assign(routeProvider.howItsWorkCaretaker)
              return null
            }}
          />
          <Route
            path="/como-funciona/busco-cuidador"
            exact={true}
            render={() => {
              window.location.assign(routeProvider.howItsWorkSearchCaretaker)
              return null
            }}
          />
          <Route
            path="/ventajas/busco-cuidador"
            exact={true}
            render={() => {
              window.location.assign(routeProvider.advantagesSearchCaretaker)
              return null
            }}
          />
          <Route
            path="/ventajas/soy-cuidador"
            exact={true}
            render={() => {
              window.location.assign(routeProvider.advantagesCaretaker)
              return null
            }}
          />
          <Route
            path="/contacto"
            exact={true}
            render={() => {
              window.location.assign(routeProvider.contactPage)
              return null
            }}
          />
          <Route path="/cuidadores" exact={true} component={CaretakerList} />
          <Route path="/mapa-clientes" exact={true} component={FullMapCaretakers} />
          <Route
            path="/notification/:extra"
            render={({ match: innerMatch }) => {
              window.location.assign(`${process.env.REACT_APP_SYMPHONY_BASE_URL}${innerMatch.url}${location.search}`)
              return null
            }}
          />
          <Route path="/chat" component={ChatRouter} />
          <Route path="/cupon-premium/crear" exact component={CreateCoupon} />
          <Route
            path="/email/desuscribir/:userId"
            exact={true}
            render={({ match: innerMatch }) => {
              return <UnsubscribeEmails userId={innerMatch.params.userId} />
            }}
          />
          <Route path="/" component={Error404} />
        </Switch>
      </Suspense>
    </>
  )
}

export default Root



// WEBPACK FOOTER //
// ./src/views/Root.tsx